import React, { useState, useCallback, useRef } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Play, Save, Share2, Download, Upload, Trash2, Settings, AlertCircle, ArrowLeft, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CustomNode from '../components/workflow/CustomNode';
import NodePalette from '../components/workflow/NodePalette';
import PropertiesPanel from '../components/workflow/PropertiesPanel';
import ExecutionPanel from '../components/workflow/ExecutionPanel';

const nodeTypes = {
  custom: CustomNode,
};

const WorkflowBuilder = () => {
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [workflowName, setWorkflowName] = useState('Nouveau Workflow');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResults, setExecutionResults] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true }, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const nodeData = JSON.parse(event.dataTransfer.getData('application/reactflow'));

      if (!nodeData) return;

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode = {
        id: `${nodeData.type}_${Date.now()}`,
        type: 'custom',
        position,
        data: {
          label: nodeData.label,
          nodeType: nodeData.type,
          icon: nodeData.icon,
          color: nodeData.color,
          config: nodeData.defaultConfig || {},
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes]
  );

  const updateNodeData = useCallback(
    (nodeId, newData) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                config: { ...node.data.config, ...newData },
              },
            };
          }
          return node;
        })
      );
    },
    [setNodes]
  );

  const deleteNode = useCallback(() => {
    if (selectedNode) {
      setNodes((nds) => nds.filter((node) => node.id !== selectedNode.id));
      setEdges((eds) => eds.filter((edge) => edge.source !== selectedNode.id && edge.target !== selectedNode.id));
      setSelectedNode(null);
    }
  }, [selectedNode, setNodes, setEdges]);

  const saveWorkflow = async () => {
    setIsSaving(true);
    try {
      const workflow = {
        name: workflowName,
        description: workflowDescription,
        nodes: nodes,
        edges: edges,
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workflow-builder/workflows`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('lawliet_token')}`,
        },
        body: JSON.stringify(workflow),
      });

      if (!response.ok) throw new Error('Erreur lors de la sauvegarde');

      const data = await response.json();
      alert('✅ Workflow sauvegardé avec succès!');
    } catch (error) {
      console.error('Error saving workflow:', error);
      alert('❌ Erreur lors de la sauvegarde du workflow');
    } finally {
      setIsSaving(false);
    }
  };

  const executeWorkflow = async () => {
    setIsExecuting(true);
    setExecutionResults(null);
    try {
      const workflow = {
        name: workflowName,
        nodes: nodes,
        edges: edges,
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workflow-builder/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('lawliet_token')}`,
        },
        body: JSON.stringify(workflow),
      });

      if (!response.ok) throw new Error('Erreur lors de l\'exécution');

      const data = await response.json();
      setExecutionResults(data);
      alert('✅ Workflow exécuté avec succès!');
    } catch (error) {
      console.error('Error executing workflow:', error);
      alert('❌ Erreur lors de l\'exécution du workflow');
    } finally {
      setIsExecuting(false);
    }
  };

  const exportWorkflow = () => {
    const workflow = {
      name: workflowName,
      description: workflowDescription,
      nodes: nodes,
      edges: edges,
    };
    const dataStr = JSON.stringify(workflow, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `${workflowName.replace(/\s+/g, '_')}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const importWorkflow = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workflow = JSON.parse(e.target.result);
          setWorkflowName(workflow.name || 'Workflow Importé');
          setWorkflowDescription(workflow.description || '');
          setNodes(workflow.nodes || []);
          setEdges(workflow.edges || []);
          alert('✅ Workflow importé avec succès!');
        } catch (error) {
          console.error('Error importing workflow:', error);
          alert('❌ Erreur lors de l\'importation du workflow');
        }
      };
      reader.readAsText(file);
    }
  };

  const clearWorkflow = () => {
    if (window.confirm('Êtes-vous sûr de vouloir effacer ce workflow ?')) {
      setNodes([]);
      setEdges([]);
      setSelectedNode(null);
      setWorkflowName('Nouveau Workflow');
      setWorkflowDescription('');
      setExecutionResults(null);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Top Toolbar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/workflows')}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Retour aux workflows"
              data-testid="workflow-builder-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Retour</span>
            </button>
            <div className="w-px h-8 bg-gray-300"></div>
            <div className="flex-1">
            <input
              type="text"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              className="text-2xl font-bold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-2"
              placeholder="Nom du workflow"
            />
            <input
              type="text"
              value={workflowDescription}
              onChange={(e) => setWorkflowDescription(e.target.value)}
              className="mt-1 text-sm text-gray-500 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-2 w-full"
              placeholder="Description du workflow (optionnel)"
            />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={executeWorkflow}
              disabled={isExecuting || nodes.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Play className="w-4 h-4" />
              {isExecuting ? 'Exécution...' : 'Exécuter'}
            </button>

            <button
              onClick={saveWorkflow}
              disabled={isSaving || nodes.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>

            <div className="border-l border-gray-300 h-8 mx-2"></div>

            <button
              onClick={exportWorkflow}
              disabled={nodes.length === 0}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
              title="Exporter le workflow"
            >
              <Download className="w-4 h-4" />
            </button>

            <label className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
              <Upload className="w-4 h-4" />
              <input
                type="file"
                accept=".json"
                onChange={importWorkflow}
                className="hidden"
              />
            </label>

            <button
              onClick={clearWorkflow}
              disabled={nodes.length === 0}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-red-600 rounded-lg hover:bg-red-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
              title="Effacer le workflow"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Node Palette - Left Sidebar */}
        <NodePalette />

        {/* Canvas Area */}
        <div className="flex-1 relative" ref={reactFlowWrapper}>
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onInit={setReactFlowInstance}
              nodeTypes={nodeTypes}
              fitView
              className="bg-gray-50"
            >
              <Background color="#e5e7eb" gap={16} />
              <Controls />
              <MiniMap
                nodeColor={(node) => node.data.color || '#6366f1'}
                className="bg-white border border-gray-300 rounded-lg"
              />

              {/* Empty State */}
              {nodes.length === 0 && (
                <Panel position="top-center" className="mt-20">
                  <div className="bg-white border border-gray-300 rounded-lg p-8 shadow-lg max-w-md text-center">
                    <Settings className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Créez votre premier workflow juridique
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Glissez-déposez des nœuds depuis la palette de gauche pour construire votre workflow d'automatisation légale personnalisé.
                    </p>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-sm text-left">
                      <p className="font-semibold text-indigo-900 mb-2 flex items-center gap-1.5"><Lightbulb className="h-4 w-4" /> Conseil :</p>
                      <ul className="text-indigo-700 space-y-1 list-disc list-inside">
                        <li>Commencez avec un nœud "Démarrer"</li>
                        <li>Ajoutez des actions (analyse, rédaction, recherche)</li>
                        <li>Connectez les nœuds pour créer un flux</li>
                        <li>Configurez chaque nœud avec le panneau de droite</li>
                      </ul>
                    </div>
                  </div>
                </Panel>
              )}
            </ReactFlow>
          </ReactFlowProvider>
        </div>

        {/* Properties Panel - Right Sidebar */}
        {selectedNode ? (
          <PropertiesPanel
            node={selectedNode}
            onUpdate={updateNodeData}
            onDelete={deleteNode}
            onClose={() => setSelectedNode(null)}
          />
        ) : (
          <div className="w-80 bg-white border-l border-gray-200 p-6">
            <div className="text-center text-gray-500 mt-20">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm">Sélectionnez un nœud pour voir ses propriétés</p>
            </div>
          </div>
        )}
      </div>

      {/* Execution Results Panel */}
      {executionResults && (
        <ExecutionPanel
          results={executionResults}
          onClose={() => setExecutionResults(null)}
        />
      )}
    </div>
  );
};

export default WorkflowBuilder;
